import React, { useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Avatar, Box, ButtonBase, Divider, ListItemIcon, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { appleTokens, tint } from '../../theme/appleTokens';
import { getColorModePreference, setColorModePreference } from '../../theme/colorMode';
import { SpaceMenuSection } from './SpaceSwitcher';

const t = appleTokens;

const APPEARANCES = [
  { value: 'light', label: 'Clair', Icon: LightModeOutlinedIcon },
  { value: 'dark', label: 'Sombre', Icon: DarkModeOutlinedIcon },
  { value: 'system', label: 'Auto', Icon: SettingsBrightnessOutlinedIcon },
];

/** Apparence de l'ERP : contrôle segmenté Clair / Sombre / Auto (système) — un choix qui change l'affichage recharge
 * la page (theme/colorMode.js). */
function AppearancePicker() {
  const [pref, setPref] = useState(getColorModePreference);
  return (
    <Box sx={{ px: 2, pt: 1.25, pb: 1.5 }}>
      <Typography sx={{ ...t.type.overline, color: t.tertiaryLabel, mb: 0.75 }}>Apparence</Typography>
      <Box
        role="radiogroup"
        aria-label="Apparence"
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, p: 0.5, borderRadius: t.radius.md, bgcolor: t.surfaceSunken, border: '1px solid', borderColor: t.separator }}
      >
        {APPEARANCES.map(({ value, label, Icon }) => {
          const selected = pref === value;
          return (
            <ButtonBase
              key={value}
              role="radio"
              aria-checked={selected}
              onClick={() => {
                setPref(value);
                setColorModePreference(value);
              }}
              sx={{
                flexDirection: 'column',
                gap: 0.25,
                py: 0.75,
                borderRadius: t.radius.sm,
                color: selected ? t.label : t.secondaryLabel,
                bgcolor: selected ? t.surface : 'transparent',
                boxShadow: selected ? t.shadow.card : 'none',
                border: '1px solid',
                borderColor: selected ? t.separator : 'transparent',
                transition: 'background-color 120ms, color 120ms',
                '&:hover': { color: t.label },
              }}
            >
              <Icon sx={{ fontSize: 18 }} />
              <Box component="span" sx={{ ...t.type.caption, fontWeight: selected ? 600 : 500 }}>
                {label}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

function initialsOf(user) {
  const fi = String(user?.firstName || '').trim().charAt(0);
  const li = String(user?.lastName || '').trim().charAt(0);
  const initials = `${fi}${li}`.toUpperCase();
  if (initials) return initials;
  const email = String(user?.email || '').trim();
  return email ? email.charAt(0).toUpperCase() : '?';
}

/** Avatar cliquable ouvrant un menu de profil — remplace le duo "texte du nom + icône déconnexion
 * nue" par un seul point d'entrée reconnaissable, comme le menu utilisateur d'une barre de menus
 * desktop (photo/initiales, identité en tête de menu, action de sortie séparée par un diviseur).
 * Les 2 modes "Gestion"/"Gestion avancée" (ex-`ManagementModeToggle`, un contrôle segmenté séparé
 * dans la topbar) vivent maintenant ici : en plein texte, ce contrôle prenait toute la largeur de la
 * topbar en vue mobile — regroupé dans ce menu déjà présent, plus de conflit de place. */
export function ProfileMenu({
  user,
  who,
  onLogout,
  advancedUnlocked,
  onRequestAdvanced,
  onLock,
  hideAdvanced = false,
  clubs = [],
  currentClubSlug = '',
  onSwitchClub,
  onSelectLeague,
}) {
  const [anchor, setAnchor] = useState(null);
  const initials = initialsOf(user);

  const modeItemSx = { py: 1.1 };
  const modeLabelSx = { ...t.type.body, fontWeight: 500, color: t.label, flexGrow: 1 };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label="Profil"
        sx={{
          p: 0.25,
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          bgcolor: 'transparent',
          lineHeight: 0,
          '&:hover': { bgcolor: t.surfaceHover },
        }}
      >
        <Avatar
          src={user?.picture || undefined}
          sx={{
            width: 32,
            height: 32,
            bgcolor: tint(t.accent, 0.45),
            color: t.accentInk,
            ...t.type.caption,
            fontWeight: 700,
          }}
        >
          {initials}
        </Avatar>
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 280, mt: 1 } } }}
      >
        {/* Identité : photo / initiales, nom, e-mail. */}
        <Box sx={{ px: 2, pt: 1.75, pb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={user?.picture || undefined} sx={{ width: 44, height: 44, bgcolor: tint(t.accent, 0.45), color: t.accentInk, fontWeight: 700 }}>
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...t.type.bodyStrong, color: t.label, lineHeight: 1.3 }} noWrap>
                {who || 'Administrateur'}
              </Typography>
              {user?.email ? (
                <Typography sx={{ ...t.type.caption, fontWeight: 400, color: t.secondaryLabel }} noWrap>
                  {user.email}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Box>
        <Divider sx={{ borderColor: t.separator }} />
        {/* Admin de la Ligue (my_clubs renvoie tous les clubs ; ErpShell ne passe des clubs qu'à lui) : l'espace — la
            Ligue ou un autre club — se choisit ici même, sans autre fenêtre. Un compte de club n'a qu'un club. */}
        {clubs.length > 0 && onSwitchClub
          ? [
            <SpaceMenuSection
              key="space"
              current={currentClubSlug}
              clubs={clubs}
              onSelectLeague={onSelectLeague}
              onSelectClub={onSwitchClub}
              onPick={(fn) => {
                setAnchor(null);
                fn?.();
              }}
            />,
            <Divider key="space-divider" sx={{ borderColor: t.separator }} />,
          ]
          : null}
        <AppearancePicker />
        <Divider sx={{ borderColor: t.separator }} />
        {/* Tableau (et non Fragment) : MUI Menu parcourt ses enfants directs pour la navigation clavier. */}
        {!hideAdvanced
          ? [
            <MenuItem
              key="mode-standard"
              onClick={() => {
                setAnchor(null);
                if (advancedUnlocked) onLock?.();
              }}
              sx={modeItemSx}
            >
              <ListItemIcon>
                <GridViewOutlinedIcon fontSize="small" sx={{ color: t.secondaryLabel }} />
              </ListItemIcon>
              <Typography sx={modeLabelSx}>Gestion</Typography>
              {!advancedUnlocked ? <CheckIcon fontSize="small" sx={{ color: t.label }} /> : null}
            </MenuItem>,
            <MenuItem
              key="mode-advanced"
              onClick={() => {
                setAnchor(null);
                if (!advancedUnlocked) onRequestAdvanced?.();
              }}
              sx={modeItemSx}
            >
              <ListItemIcon>
                <ShieldOutlinedIcon fontSize="small" sx={{ color: t.secondaryLabel }} />
              </ListItemIcon>
              <Typography sx={modeLabelSx}>Gestion avancée</Typography>
              {advancedUnlocked ? <CheckIcon fontSize="small" sx={{ color: t.label }} /> : null}
            </MenuItem>,
            <Divider key="mode-divider" sx={{ borderColor: t.separator }} />,
          ]
          : null}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            onLogout();
          }}
          sx={{ py: 1.1, my: 0.25, '&:hover': { bgcolor: tint(t.status.injured.color, 0.08) } }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: t.status.injured.color }} />
          </ListItemIcon>
          <Typography sx={{ ...t.type.body, fontWeight: 600, color: t.status.injured.color }}>Se déconnecter</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
